// Chip-based filter search for the Activity feed — the v2 take on the legacy
// OmniSearch. Typed terms resolve to categorized chips (app / service / env /
// type / user); free text becomes a deployment-ID search chip.

import React, { useMemo } from 'react';
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from '@mui/material/styles';
import { getColors } from '../theme/tokens';
import { serviceFilterLabel } from '../lib/records';

export type FilterKind = 'app' | 'service' | 'env' | 'type' | 'user' | 'deploymentId';

export interface FilterOption {
  kind: FilterKind;
  /** Stored/matched identity — for 'service', a type-qualified serviceFilterKey. */
  value: string;
  /** What's actually shown to the user; defaults to `value` when absent. */
  label?: string;
}

const KIND_META: Record<FilterKind, { label: string; color: string }> = {
  app: { label: 'App', color: '#0075de' },
  service: { label: 'Service', color: '#8155b8' },
  env: { label: 'Env', color: '#dd5b00' },
  type: { label: 'Type', color: '#2a9d99' },
  user: { label: 'User', color: '#787774' },
  deploymentId: { label: 'ID', color: '#d93a3a' },
};

export interface FilterSearchProps {
  apps: string[];
  services: string[];
  envs: string[];
  types: string[];
  users: string[];
  selectedApps: string[];
  selectedServices: string[];
  selectedEnvs: string[];
  selectedTypes: string[];
  selectedUsers: string[];
  depId: string;
  setApps: (v: string[]) => void;
  setServices: (v: string[]) => void;
  setEnvs: (v: string[]) => void;
  setTypes: (v: string[]) => void;
  setUsers: (v: string[]) => void;
  setDepId: (v: string) => void;
}

const optionKey = (o: FilterOption) => `${o.kind}:${o.value}`;

const FilterSearch: React.FC<FilterSearchProps> = props => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const {
    apps,
    services,
    envs,
    types,
    users,
    selectedApps,
    selectedServices,
    selectedEnvs,
    selectedTypes,
    selectedUsers,
    depId,
    setApps,
    setServices,
    setEnvs,
    setTypes,
    setUsers,
    setDepId,
  } = props;

  const options = useMemo<FilterOption[]>(() => {
    const out: FilterOption[] = [];
    apps.forEach(v => out.push({ kind: 'app', value: v }));
    services.forEach(v => out.push({ kind: 'service', value: v, label: serviceFilterLabel(v) }));
    envs.forEach(v => out.push({ kind: 'env', value: v }));
    types.forEach(v => out.push({ kind: 'type', value: v }));
    users.forEach(v => out.push({ kind: 'user', value: v }));
    return out;
  }, [apps, services, envs, types, users]);

  const value = useMemo<FilterOption[]>(() => {
    const chips: FilterOption[] = [];
    selectedApps.forEach(v => chips.push({ kind: 'app', value: v }));
    selectedServices.forEach(v => chips.push({ kind: 'service', value: v, label: serviceFilterLabel(v) }));
    selectedEnvs.forEach(v => chips.push({ kind: 'env', value: v }));
    selectedTypes.forEach(v => chips.push({ kind: 'type', value: v }));
    selectedUsers.forEach(v => chips.push({ kind: 'user', value: v }));
    if (depId.trim()) chips.push({ kind: 'deploymentId', value: depId.trim() });
    return chips;
  }, [selectedApps, selectedServices, selectedEnvs, selectedTypes, selectedUsers, depId]);

  const handleChange = (_: unknown, next: (FilterOption | string)[]) => {
    const normalized: FilterOption[] = next.map(item =>
      typeof item === 'string' ? { kind: 'deploymentId' as const, value: item.trim() } : item
    );
    const bucket = (kind: FilterKind) =>
      normalized.filter(o => o.kind === kind).map(o => o.value);

    const ids = bucket('deploymentId');
    const newId = ids.length > 0 ? ids[ids.length - 1] : '';

    // Element-wise so values containing '|' can't collide into false equality.
    const same = (a: string[], b: string[]) =>
      a.length === b.length && a.every((v, i) => v === b[i]);
    if (!same(bucket('app'), selectedApps)) setApps(bucket('app'));
    if (!same(bucket('service'), selectedServices)) setServices(bucket('service'));
    if (!same(bucket('env'), selectedEnvs)) setEnvs(bucket('env'));
    if (!same(bucket('type'), selectedTypes)) setTypes(bucket('type'));
    if (!same(bucket('user'), selectedUsers)) setUsers(bucket('user'));
    if (newId !== depId) setDepId(newId);
  };

  return (
    <Autocomplete<FilterOption, true, false, true>
      multiple
      freeSolo
      size="small"
      fullWidth
      options={options}
      value={value}
      onChange={handleChange}
      isOptionEqualToValue={(a, b) =>
        typeof a !== 'string' && typeof b !== 'string' && optionKey(a) === optionKey(b)
      }
      getOptionLabel={o => (typeof o === 'string' ? o : o.label ?? o.value)}
      groupBy={o => KIND_META[o.kind].label}
      filterOptions={(opts, state) => {
        const q = state.inputValue.toLowerCase().trim();
        const selectedKeys = new Set(value.map(optionKey));
        const unselected = opts.filter(o => !selectedKeys.has(optionKey(o)));
        if (!q) return unselected.slice(0, 50);
        return unselected.filter(o => (o.label ?? o.value).toLowerCase().includes(q)).slice(0, 50);
      }}
      renderOption={(props_, option) => (
        <li {...props_} key={optionKey(option)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Chip
              label={KIND_META[option.kind].label}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.62rem',
                fontWeight: 700,
                letterSpacing: '0.05em',
                backgroundColor: `${KIND_META[option.kind].color}1a`,
                color: KIND_META[option.kind].color,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
            <Typography variant="body2">{option.label ?? option.value}</Typography>
          </Box>
        </li>
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          const meta =
            typeof option === 'string' ? KIND_META.deploymentId : KIND_META[option.kind];
          const label =
            typeof option === 'string'
              ? `ID: ${option}`
              : `${meta.label}: ${option.label ?? option.value}`;
          return (
            <Chip
              key={key}
              {...tagProps}
              label={label}
              size="small"
              sx={{
                height: 22,
                fontWeight: 600,
                fontSize: '0.72rem',
                backgroundColor: `${meta.color}14`,
                color: theme.palette.mode === 'dark' ? c.ink : meta.color,
                border: `1px solid ${meta.color}44`,
              }}
            />
          );
        })
      }
      renderInput={params => (
        <TextField
          {...params}
          placeholder={
            value.length === 0 ? 'Filter by app, service, env, type, user — or paste an ID' : ''
          }
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <SearchIcon sx={{ fontSize: 18, color: c.inkFaint, ml: 0.5, mr: 0.5 }} />
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default FilterSearch;
